import React, {useCallback, useEffect, useState} from 'react';
import {formatDateTime} from '../utils/dateTime';
import {observer} from 'mobx-react-lite';
import createAxiosInstance from '../support/AxiosSupport';
import Spinner from '../components/Spinner';
import ExportWardrobeButton from '../components/ExportWardrobeButton';
import AccountDeletionBlock from '../components/privacy/AccountDeletionBlock';
import GoogleCalendarCard from '../components/privacy/GoogleCalendarCard';
import FullExportZipBlock from '../components/privacy/FullExportZipBlock';
import {
    DownloadIcon,
    FileTextIcon,
    LinkIcon,
    RefreshIcon,
    SendIcon,
    ShieldIcon,
    TrashIcon,
} from '../components/icons';
import {useDocumentTitle} from '../hooks/useDocumentTitle';

/**
 * Privacy Center — центр конфиденциальности и данных (CTHS-227, Epic CTHS-165).
 *
 * Единое место реализации прав субъекта ПДн (152-ФЗ): управление согласиями, история правовых
 * документов, подключённые интеграции, экспорт/удаление данных. Маршрут {@code /profile/privacy}.
 *
 * Каждая из 4 секций самостоятельно держит состояния loading/empty/error (C-6.3). Согласия
 * меняются через {@code PATCH /api/privacy/consent} (по одному полю за раз). Блокирующие API
 * (история документов — CTHS-222) деградируют мягко: 404/ошибка → empty-state. Согласие на
 * аналитику (CTHS-223) и удаление аккаунта (CTHS-229) — рабочие, не заглушки.
 */

// ---------- Типы ----------

interface Consent {
    aiProfilingConsent: boolean;
    recommendationsConsent: boolean;
    locationConsent: boolean;
    googleCalendarConsent: boolean;
    photoProfilingConsent: boolean;
    marketingConsent: boolean;
    analyticsConsent: boolean;
    configured: boolean;
    updatedAt: string | null;
}

type ConsentKey =
    | 'aiProfilingConsent'
    | 'recommendationsConsent'
    | 'locationConsent'
    | 'googleCalendarConsent'
    | 'photoProfilingConsent'
    | 'marketingConsent'
    | 'analyticsConsent';

interface ConsentRow {
    key: ConsentKey;
    label: string;
    hint: string;
}

interface FutureConsentRow {
    key: string;
    label: string;
    hint: string;
}

interface DocumentAcceptance {
    id?: string;
    acceptedAt?: string | null;
    version?: string | null;
    ipAddress?: string | null;
    documentUrl?: string | null;
    title?: string | null;
    /** Откуда пришло согласие — `LegalDocumentAcceptance.AcceptanceSource` (legal-documents §9). */
    source?: string | null;
}

const ACCEPTANCE_SOURCE_LABELS: Record<string, string> = {
    REGISTRATION: 'при регистрации',
    RECONSENT: 'повторное согласие',
};

interface TelegramStatus {
    linked: boolean;
    username: string | null;
    linkedAt: string | null;
}

type SectionState = 'loading' | 'ready' | 'error';

// ---------- Конфигурация секции согласий ----------

const CONSENT_ROWS: ConsentRow[] = [
    {
        key: 'aiProfilingConsent',
        label: 'Профилирование поведения',
        hint: 'Анализ носибельности, рейтингов и активности для аналитики гардероба.',
    },
    {
        key: 'recommendationsConsent',
        label: 'Персональные рекомендации',
        hint: 'Формирование рекомендаций: что выбыть, что докупить, образы.',
    },
    {
        key: 'locationConsent',
        label: 'Погодный контур / геолокация',
        hint: 'Использование локации для подбора образов по погоде.',
    },
    {
        key: 'googleCalendarConsent',
        label: 'Google Calendar',
        hint: 'Обработка событий календаря для планирования образов.',
    },
    {
        key: 'photoProfilingConsent',
        label: 'Анализ фото в чате',
        hint: 'AI-профилирование при анализе фото вещей в чате (GDPR). Лица не обрабатываются.',
    },
    {
        key: 'marketingConsent',
        label: 'Рассылки на почту',
        hint: 'Новости продукта и подборки. Письма про регистрацию, вход и безопасность приходят всегда.',
    },
    {
        key: 'analyticsConsent',
        label: 'Аналитика использования',
        hint: 'Обезличенная статистика использования — помогает улучшать продукт. На рекомендации не влияет.',
    },
];

// Согласие без точки enforcement на бэкенде — disabled-заглушка: показываем, что оно
// планируется, но не делаем вид, что переключатель на что-то влияет (см. requirements §7).
const FUTURE_CONSENT_ROWS: FutureConsentRow[] = [
    {
        key: 'aiPhotosConsent',
        label: 'Фотораспознавание AI',
        hint: 'Автоматическое распознавание вещей на фото. Появится позже.',
    },
    // «Маркетинговые коммуникации» и «Аналитика использования» больше не заглушки:
    // оба согласия живут выше, среди действующих переключателей (CTHS-223).
];

// ---------- Вспомогательные компоненты ----------

const SectionCard: React.FC<{
    icon: React.ReactNode;
    title: string;
    description?: string;
    testid: string;
    children: React.ReactNode;
}> = ({icon, title, description, testid, children}) => (
    <section className="card p-5 sm:p-6 space-y-4" data-testid={testid}>
        <div className="flex items-start gap-3">
            <span className="text-gray-500 dark:text-gray-400 mt-0.5 shrink-0">{icon}</span>
            <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
                {description && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{description}</p>
                )}
            </div>
        </div>
        {children}
    </section>
);

const LoadingState: React.FC<{testid: string}> = ({testid}) => (
    <div className="py-4" data-testid={testid}>
        <Spinner/>
    </div>
);

const ErrorState: React.FC<{testid: string; onRetry: () => void}> = ({testid, onRetry}) => (
    <div className="space-y-3" data-testid={testid}>
        <p className="text-sm text-red-600 dark:text-red-400">
            Не удалось загрузить данные. Попробуйте ещё раз.
        </p>
        <button type="button" onClick={onRetry} className="btn-secondary min-h-[44px]">
            <RefreshIcon className="inline-block mr-2" size={18}/>
            Повторить
        </button>
    </div>
);

const EmptyState: React.FC<{testid: string; message: string}> = ({testid, message}) => (
    <p className="text-sm text-gray-500 dark:text-gray-400 py-2" data-testid={testid}>
        {message}
    </p>
);

/** Доступный тумблер согласия с тач-зоной ≥ 44×44 px (AC-8). */
const ConsentToggle: React.FC<{
    label: string;
    hint: string;
    checked: boolean;
    disabled?: boolean;
    busy?: boolean;
    onChange: (value: boolean) => void;
    testid: string;
}> = ({label, hint, checked, disabled = false, busy = false, onChange, testid}) => (
    <div className="flex items-start justify-between gap-4 py-2">
        <div className="min-w-0">
            <p className="text-gray-900 dark:text-gray-100">{label}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">{hint}</p>
        </div>
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            aria-label={label}
            disabled={disabled || busy}
            onClick={() => onChange(!checked)}
            data-testid={testid}
            className={`relative shrink-0 inline-flex items-center h-7 w-12 min-h-[44px] sm:min-h-0 rounded-full
                transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-brand-500/40
                disabled:opacity-50 disabled:cursor-not-allowed
                ${checked ? 'bg-brand-600' : 'bg-gray-300 dark:bg-ink-700'}`}
        >
            <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-200
                    ${checked ? 'translate-x-6' : 'translate-x-1'}`}
            />
        </button>
    </div>
);

const formatDate = (iso: string | null | undefined): string => formatDateTime(iso, undefined, '');

// ---------- Секция «Согласия» ----------

const ConsentsSection: React.FC = () => {
    const [state, setState] = useState<SectionState>('loading');
    const [consent, setConsent] = useState<Consent | null>(null);
    const [pendingKey, setPendingKey] = useState<ConsentKey | null>(null);
    const [saveError, setSaveError] = useState(false);

    const load = useCallback(async () => {
        setState('loading');
        try {
            const api = await createAxiosInstance();
            const res = await api.get<Consent>('/privacy/consent');
            setConsent(res.data);
            setState('ready');
        } catch {
            setState('error');
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const toggle = async (key: ConsentKey, value: boolean) => {
        setPendingKey(key);
        setSaveError(false);
        try {
            const api = await createAxiosInstance();
            const res = await api.patch<Consent>('/privacy/consent', {[key]: value});
            setConsent(res.data);
        } catch {
            setSaveError(true);
        } finally {
            setPendingKey(null);
        }
    };

    return (
        <SectionCard
            icon={<ShieldIcon size={22}/>}
            title="Согласия"
            description="Управляйте тем, как система обрабатывает ваши данные. Изменения вступают в силу сразу."
            testid="section-consents"
        >
            {state === 'loading' && <LoadingState testid="consents-loading"/>}
            {state === 'error' && <ErrorState testid="consents-error" onRetry={load}/>}
            {state === 'ready' && consent && (
                <div className="divide-y divide-gray-100 dark:divide-white/5">
                    {CONSENT_ROWS.map(row => (
                        <ConsentToggle
                            key={row.key}
                            label={row.label}
                            hint={row.hint}
                            checked={consent[row.key]}
                            busy={pendingKey === row.key}
                            onChange={value => toggle(row.key, value)}
                            testid={`consent-${row.key}`}
                        />
                    ))}
                    {FUTURE_CONSENT_ROWS.map(row => (
                        <ConsentToggle
                            key={row.key}
                            label={row.label}
                            hint={row.hint}
                            checked={false}
                            disabled
                            onChange={() => undefined}
                            testid={`consent-${row.key}`}
                        />
                    ))}
                    {saveError && (
                        <p className="text-sm text-red-600 dark:text-red-400 pt-3" data-testid="consents-save-error">
                            Не удалось сохранить изменение. Попробуйте ещё раз.
                        </p>
                    )}
                </div>
            )}
        </SectionCard>
    );
};

// ---------- Секция «История документов» ----------

const DocumentsSection: React.FC = () => {
    const [state, setState] = useState<SectionState>('loading');
    const [items, setItems] = useState<DocumentAcceptance[]>([]);

    const load = useCallback(async () => {
        setState('loading');
        try {
            const api = await createAxiosInstance();
            const res = await api.get<DocumentAcceptance[]>('/documents/acceptance/history');
            setItems(Array.isArray(res.data) ? res.data : []);
            setState('ready');
        } catch (e: unknown) {
            // CTHS-222 ещё не реализован: 404/503 — это ожидаемая «пустая история», а не ошибка.
            const status = (e as {response?: {status?: number}})?.response?.status;
            if (status === 404 || status === 501 || status === 503) {
                setItems([]);
                setState('ready');
            } else {
                setState('error');
            }
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    return (
        <SectionCard
            icon={<FileTextIcon size={22}/>}
            title="История документов"
            description="Принятые правовые документы: дата, версия, источник."
            testid="section-documents"
        >
            {state === 'loading' && <LoadingState testid="documents-loading"/>}
            {state === 'error' && <ErrorState testid="documents-error" onRetry={load}/>}
            {state === 'ready' && items.length === 0 && (
                <EmptyState testid="documents-empty" message="История пуста."/>
            )}
            {state === 'ready' && items.length > 0 && (
                <ul className="divide-y divide-gray-100 dark:divide-white/5" data-testid="documents-list">
                    {items.map((doc, idx) => (
                        <li key={doc.id ?? idx} className="py-3 flex items-start justify-between gap-4">
                            <div className="min-w-0">
                                <p className="text-gray-900 dark:text-gray-100">
                                    {doc.title ?? 'Документ'}
                                    {doc.version && (
                                        <span className="ml-2 chip">v{doc.version}</span>
                                    )}
                                </p>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    {formatDate(doc.acceptedAt)}
                                    {doc.source && ACCEPTANCE_SOURCE_LABELS[doc.source] && (
                                        <span className="ml-2">· {ACCEPTANCE_SOURCE_LABELS[doc.source]}</span>
                                    )}
                                    {doc.ipAddress && <span className="ml-2">· IP {doc.ipAddress}</span>}
                                </p>
                            </div>
                            {doc.documentUrl && (
                                <a
                                    href={doc.documentUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm text-primary-600 dark:text-primary-400 hover:underline shrink-0 min-h-[44px] inline-flex items-center"
                                >
                                    Текст
                                </a>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </SectionCard>
    );
};

// ---------- Секция «Интеграции» ----------

const IntegrationsSection: React.FC = () => {
    const [state, setState] = useState<SectionState>('loading');
    const [telegram, setTelegram] = useState<TelegramStatus>({linked: false, username: null, linkedAt: null});
    const [telegramBusy, setTelegramBusy] = useState(false);

    const load = useCallback(async () => {
        setState('loading');
        try {
            const api = await createAxiosInstance();
            // Статус тянем мягко: недоступность интеграции не должна ломать всю секцию.
            // Google Calendar собой занимается сам — у него свой сценарий подключения.
            const tg = await api.get<TelegramStatus>('/telegram/status')
                .then(r => r.data).catch(() => null);
            setTelegram(tg ?? {linked: false, username: null, linkedAt: null});
            setState('ready');
        } catch {
            setState('error');
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const disconnectTelegram = async () => {
        setTelegramBusy(true);
        try {
            const api = await createAxiosInstance();
            await api.delete('/telegram/link');
            setTelegram({linked: false, username: null, linkedAt: null});
        } catch {
            // оставляем текущий статус; пользователь может повторить
        } finally {
            setTelegramBusy(false);
        }
    };

    const integrationRow = (
        icon: React.ReactNode,
        name: string,
        connected: boolean,
        meta: string,
        busy: boolean,
        onDisconnect: () => void,
        testid: string,
    ) => (
        <div className="flex items-center justify-between gap-4 py-3" data-testid={testid}>
            <div className="flex items-center gap-3 min-w-0">
                <span className="text-gray-500 dark:text-gray-400 shrink-0">{icon}</span>
                <div className="min-w-0">
                    <p className="text-gray-900 dark:text-gray-100">{name}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        {connected ? (meta || 'Подключено') : 'Не подключено'}
                    </p>
                </div>
            </div>
            {connected && (
                <button
                    type="button"
                    onClick={onDisconnect}
                    disabled={busy}
                    className="btn-secondary min-h-[44px] shrink-0"
                    data-testid={`${testid}-disconnect`}
                >
                    <TrashIcon className="inline-block mr-2" size={18}/>
                    {busy ? 'Отключаем…' : 'Отключить'}
                </button>
            )}
        </div>
    );

    return (
        <SectionCard
            icon={<LinkIcon size={22}/>}
            title="Интеграции"
            description="Подключённые сервисы. Отключение прекращает обмен данными."
            testid="section-integrations"
        >
            {state === 'loading' && <LoadingState testid="integrations-loading"/>}
            {state === 'error' && <ErrorState testid="integrations-error" onRetry={load}/>}
            {state === 'ready' && (
                <div className="divide-y divide-gray-100 dark:divide-white/5">
                    {/* Google Calendar — не строка «подключено/отключить», а карточка со своим
                        сценарием: согласие до OAuth, выбор календарей, ручная синхронизация
                        (CTHS-174). Держать это внутри общей строки интеграций не получилось бы. */}
                    <GoogleCalendarCard/>
                    {integrationRow(
                        <SendIcon size={20}/>,
                        'Telegram',
                        telegram.linked,
                        telegram.username ? `Подключено: ${telegram.username}` : 'Подключено',
                        telegramBusy,
                        disconnectTelegram,
                        'integration-telegram',
                    )}
                </div>
            )}
        </SectionCard>
    );
};

// ---------- Секция «Данные аккаунта» ----------

const AccountDataSection: React.FC = () => {
    return (
        <SectionCard
            icon={<DownloadIcon size={22}/>}
            title="Данные аккаунта"
            description="Скачайте копию своих данных или удалите аккаунт."
            testid="section-account"
        >
            <div className="space-y-5">
                <div>
                    <h3 className="text-base font-medium text-gray-900 dark:text-gray-100 mb-2">
                        Экспорт данных
                    </h3>
                    <ExportWardrobeButton/>
                </div>

                <div className="border-t border-gray-100 dark:border-white/5 pt-5">
                    <h3 className="text-base font-medium text-gray-900 dark:text-gray-100 mb-2">
                        Архив с фотографиями
                    </h3>
                    <FullExportZipBlock/>
                </div>

                <div className="border-t border-gray-100 dark:border-white/5 pt-5">
                    <h3 className="text-base font-medium text-gray-900 dark:text-gray-100 mb-2">
                        Удаление аккаунта
                    </h3>
                    <AccountDeletionBlock/>
                </div>
            </div>
        </SectionCard>
    );
};

// ---------- Страница ----------

const PrivacyCenterPage: React.FC = () => {
    useDocumentTitle('Конфиденциальность и данные');
    return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="privacy-center">
            <header className="flex items-center gap-3">
                <ShieldIcon size={26} className="text-gray-700 dark:text-gray-200"/>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                        Конфиденциальность и данные
                    </h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        Управление согласиями, документами, интеграциями и данными аккаунта.
                    </p>
                </div>
            </header>

            <ConsentsSection/>
            <DocumentsSection/>
            <IntegrationsSection/>
            <AccountDataSection/>
        </div>
    );
};

export {ConsentsSection, DocumentsSection, IntegrationsSection, AccountDataSection};
export default observer(PrivacyCenterPage);
